import React from 'react';
import { styles } from './Coupon.css';
import Card from '@jbhi-fi/lanyard-ui/components/Card';
import {
  PerksCoupon,
  SpendAndSaveIllustrated,
  Positive,
  Close,
  Warning,
  ArrowRight,
} from '@jbhi-fi/lanyard-ui/components/Icons';
import TickIcon from '@jbhi-fi/lanyard-ui/components/Icons/Tick';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { getFriendlyFormattedDate } from '@jbhi-fi/utils/src/date/getFriendlyFormattedDate';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import PromoTag from '@jbhi-fi/lanyard-ui/components/PromoTag';

type CouponProps = {
  viewMode: 'full' | 'mini';
  type: CouponType;
  name: string;
  number: string;
  expiry: string;
  isApplied: boolean;
  isInvalid?: boolean;
  isBestDeal?: boolean;
  tag?: string;
  success: boolean;
  onApplyCoupon: (number: string) => void;
  onRemoveCoupon?: (number: string) => void;
};

type CouponType = 'perks' | 'spendAndSave';

type CouponActionsProps = {
  number: string;
  isApplied: boolean;
  onApplyCoupon: (e: React.MouseEvent, number: string) => void;
  onRemoveCoupon?: (number: string) => void;
};

const CouponActions = ({ isApplied, number, onApplyCoupon, onRemoveCoupon }: CouponActionsProps) => {
  const [isRemovingCoupon, setIsRemovingCoupon] = React.useState(false);

  const handleRemoveCoupon = async (couponCode: string) => {
    setIsRemovingCoupon(true);
    if (onRemoveCoupon) {
      await onRemoveCoupon(couponCode);
    }
    setIsRemovingCoupon(false);
  };

  if (onRemoveCoupon)
    return (
      <Button
        style={{ padding: 0 }}
        classes={isRemovingCoupon ? styles.removeActionButton : ''}
        onClick={e => (isApplied ? handleRemoveCoupon(number) : onApplyCoupon(e, number))}
        type="iconOnly"
        icon={isApplied ? <Close className={styles.closeIcon} /> : <Positive className={styles.positiveIcon} />}
        loading={isRemovingCoupon}
      />
    );
  else {
    if (!onRemoveCoupon && isApplied) {
      return (
        <Button
          style={{ padding: 0 }}
          onClick={e => onApplyCoupon(e, number)}
          type="iconOnly"
          icon={<TickIcon className={styles.appliedIcon} />}
        />
      );
    }
  }

  return !isApplied ? (
    <Button
      style={{ padding: 0 }}
      onClick={e => onApplyCoupon(e, number)}
      type="iconOnly"
      icon={<Positive className={styles.positiveIcon} />}
    />
  ) : null;
};

const CouponIcon = ({ type, tag }: { type: string; tag: string | undefined }) => {
  if (type === 'spendAndSave') return <SpendAndSaveIllustrated />;
  if (tag) return <PromoTag size="sm" label={tag} variant="main" />;
  return <PerksCoupon className={styles.perksIcon} />;
};

const BEST_DEAL = 'Best Deal';

export const Coupon = ({
  viewMode = 'full',
  type = 'perks',
  name,
  number,
  expiry,
  isApplied,
  isBestDeal,
  tag,
  success,
  onApplyCoupon,
  onRemoveCoupon,
}: CouponProps) => {
  const handleApplyCoupon = (e: React.MouseEvent, couponCode: string) => {
    e.preventDefault();
    onApplyCoupon(couponCode);
  };

  return viewMode === 'mini' ? (
    <Card
      className={styles.container({ viewMode })}
      spacing={0}
      shadow={2}
      backgroundColor={isApplied ? palette.secondary.main : palette.primary.light}
      onClick={e => (!isApplied ? handleApplyCoupon(e, number) : null)}
    >
      <div className={styles.content({ viewMode })}>
        <Button
          classes={styles.couponTitle}
          onClick={e => (isApplied ? handleApplyCoupon(e, number) : null)}
          type="link"
          size="sm"
        >
          {!success && isApplied ? <Warning className={styles.warningIcon} /> : undefined}
          <CouponIcon type={type} tag={isBestDeal ? BEST_DEAL : tag} />
          {name}
        </Button>
        <CouponActions
          number={number}
          isApplied={isApplied}
          onApplyCoupon={handleApplyCoupon}
          onRemoveCoupon={onRemoveCoupon}
        />
      </div>
    </Card>
  ) : (
    <Card className={styles.container({ viewMode })} spacing={0} shadow={2} backgroundColor={palette.secondary.main}>
      <div className={styles.content({ viewMode })}>
        <div className={styles.textContent}>
          <CalloutFont className={styles.couponName} as="h6">
            {name}
          </CalloutFont>
          <span className={styles.expiryDate}>Expires {getFriendlyFormattedDate(expiry)}</span>
          <Button classes={styles.link} onClick={e => handleApplyCoupon(e, number)} type="link" size="sm">
            View T&Cs and exclusions
          </Button>
        </div>
        {isApplied ? (
          <span className={styles.appliedStatus}>
            <Button
              classes={styles.appliedStatus}
              onClick={e => handleApplyCoupon(e, number)}
              type="linkWithIcon"
              icon={<TickIcon />}
              iconPosition="left"
              size="sm"
            >
              Applied!
            </Button>
          </span>
        ) : (
          <Button
            type="linkWithIcon"
            classes={styles.actionButton}
            onClick={e => handleApplyCoupon(e, number)}
            label="Redeem now"
            icon={<ArrowRight />}
            iconPosition="right"
            size="sm"
          />
        )}
      </div>
    </Card>
  );
};
